import { Box, Typography } from '@mui/material';
import { colorTokens, radiusTokens, typographyTokens } from '../../app/designTokens';

const { semantic } = colorTokens;

/**
 * Compact empty / unavailable metric placeholder for dashboards.
 */
export function DashboardEmptyState({ message, sx }) {
  return (
    <Box
      sx={{
        py: 2.5,
        px: 1,
        textAlign: 'center',
        ...sx,
      }}
    >
      <Typography
        sx={{
          fontFamily: typographyTokens.fontFamily,
          fontSize: 13,
          lineHeight: '18px',
          color: semantic.text.muted,
        }}
      >
        {message}
      </Typography>
    </Box>
  );
}

/**
 * CSS segmented bar for status mix (ready / warning / danger / brand / neutral).
 * @param {{ key: string, label: string, value: number, color: string }[]} segments
 */
export function MiniSegmentedBar({ segments = [], height = 10 }) {
  const total = segments.reduce((sum, item) => sum + (Number(item.value) || 0), 0);

  if (total <= 0) {
    return (
      <Box
        sx={{
          height,
          borderRadius: `${radiusTokens.pill}px`,
          bgcolor: semantic.action.secondary.bg,
          border: `1px solid ${semantic.border.subtle}`,
        }}
      />
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height,
          borderRadius: `${radiusTokens.pill}px`,
          overflow: 'hidden',
          bgcolor: semantic.action.secondary.bg,
          border: `1px solid ${semantic.border.subtle}`,
        }}
      >
        {segments.map((segment) => {
          const value = Number(segment.value) || 0;
          if (value <= 0) return null;
          return (
            <Box
              key={segment.key}
              title={`${segment.label}: ${value}`}
              sx={{
                width: `${(value / total) * 100}%`,
                bgcolor: segment.color,
                minWidth: value > 0 ? 4 : 0,
              }}
            />
          );
        })}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          mt: 1.5,
        }}
      >
        {segments.map((segment) => (
          <Box
            key={`${segment.key}-legend`}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: segment.color,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontSize: 12,
                lineHeight: '16px',
                color: semantic.text.secondary,
              }}
            >
              {segment.label}
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                lineHeight: '16px',
                color: semantic.text.primary,
                fontWeight: 600,
              }}
            >
              {Number(segment.value) || 0}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/**
 * Simple SVG donut for status distribution. No chart library.
 */
export function MiniStatusDonut({
  segments = [],
  size = 112,
  strokeWidth = 14,
  centerLabel,
  centerValue,
}) {
  const total = segments.reduce((sum, item) => sum + (Number(item.value) || 0), 0);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  if (total <= 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 2, md: 3 },
        flexWrap: 'wrap',
        width: '100%',
      }}
    >
      <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={semantic.action.secondary.bg}
            strokeWidth={strokeWidth}
          />
          {segments.map((segment) => {
            const value = Number(segment.value) || 0;
            if (value <= 0) return null;
            const length = (value / total) * circumference;
            const node = (
              <circle
                key={segment.key}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={segment.color}
                strokeWidth={strokeWidth}
                strokeDasharray={`${length} ${circumference - length}`}
                strokeDashoffset={-offset}
                strokeLinecap="butt"
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
              />
            );
            offset += length;
            return node;
          })}
        </svg>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          {centerValue != null && (
            <Typography
              sx={{
                fontFamily: typographyTokens.fontFamilyDisplay,
                fontWeight: typographyTokens.fontWeightBold,
                fontSize: 20,
                lineHeight: '24px',
                color: semantic.text.primary,
              }}
            >
              {centerValue}
            </Typography>
          )}
          {centerLabel && (
            <Typography
              sx={{
                fontSize: 11,
                lineHeight: '14px',
                color: semantic.text.muted,
                textAlign: 'center',
                px: 1,
              }}
            >
              {centerLabel}
            </Typography>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          minWidth: 0,
          flex: '1 1 160px',
        }}
      >
        {segments.map((segment) => (
          <Box
            key={`donut-${segment.key}`}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: segment.color,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontSize: 12,
                lineHeight: '16px',
                color: semantic.text.secondary,
                flex: 1,
                minWidth: 0,
              }}
            >
              {segment.label}
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                lineHeight: '16px',
                fontWeight: typographyTokens.fontWeightSemibold,
                color: semantic.text.primary,
                flexShrink: 0,
              }}
            >
              {Number(segment.value) || 0}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/**
 * Placeholder when historical trends are not available.
 */
export function MiniTrendPlaceholder({ message }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: `${radiusTokens.md}px`,
        border: `1px dashed ${semantic.border.subtle}`,
        bgcolor: semantic.bg.input,
      }}
    >
      <Box
        sx={{
          height: 48,
          mb: 1.5,
          borderRadius: `${radiusTokens.sm}px`,
          background: `linear-gradient(90deg, ${semantic.action.secondary.bg} 0%, ${semantic.bg.surface} 50%, ${semantic.action.secondary.bg} 100%)`,
          opacity: 0.7,
        }}
      />
      <Typography
        sx={{
          fontSize: 12,
          lineHeight: '16px',
          color: semantic.text.muted,
        }}
      >
        {message}
      </Typography>
    </Box>
  );
}
